import { buildCoachTimeline } from '../coachTimeline';
import { coachDayKey } from './coachDayKey';
import { buildCoachProgression } from './coachStrategyProgression';

const timestamp = value => {
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const unwrapInsight = insight => {
  if (!insight) return null;
  return insight.data ? { ...insight, ...insight.data } : insight;
};

export const buildCoachInsightActions = (insight) => {
  const actions = [];
  if (Array.isArray(insight?.workout_recommendations) && insight.workout_recommendations.length > 0) {
    actions.push({ type: 'workout', icon: '🏋️', label: 'Workout today', items: insight.workout_recommendations });
  }
  if (Array.isArray(insight?.meal_recommendations) && insight.meal_recommendations.length > 0) {
    actions.push({ type: 'nutrition', icon: '🥦', label: 'Nutrition', items: insight.meal_recommendations });
  }
  if (Array.isArray(insight?.recovery_recommendations) && insight.recovery_recommendations.length > 0) {
    actions.push({ type: 'recovery', icon: '🧘', label: 'Recovery today', items: insight.recovery_recommendations });
  }
  if (Array.isArray(insight?.media_follow_up_requests) && insight.media_follow_up_requests.length > 0) {
    const planStatus = insight?.media_follow_up_plan_status;
    const activityNames = Array.isArray(planStatus?.activity_names) ? planStatus.activity_names.filter(Boolean) : [];
    const isAlreadyRequired = planStatus?.status === 'already_required';
    const activityLabel = activityNames.length === 1 ? activityNames[0] : 'Your recurring media activity';
    actions.push({
      type: 'media',
      icon: '📸',
      label: 'Better feedback next time',
      items: insight.media_follow_up_requests.slice(0, 3),
      ...(isAlreadyRequired ? {
        plan_status: 'already_required',
        status_label: 'Already in your plan',
        impact_notice: `This is already required by ${activityLabel}, so there is no new change to approve.`,
      } : {}),
    });
  }
  if (Array.isArray(insight?.ai_actions_applied)) actions.push(...insight.ai_actions_applied);
  return actions;
};

export const coachMessageFromInsight = (rawInsight) => {
  const insight = unwrapInsight(rawInsight);
  if (!insight) return null;
  const isActivityReply = insight.kind === 'activity_update';
  const paragraphs = (isActivityReply
    ? [insight.general_advice || insight.strategy_assessment?.status_summary]
    : [insight.general_advice, insight.strategy_assessment?.status_summary, insight.strategy_nudge]
  ).filter(value => typeof value === 'string' && value.trim()).map(value => value.trim());
  const actions = buildCoachInsightActions(insight);
  if (paragraphs.length === 0 && actions.length === 0) return null;
  return {
    id: String(insight._id || `insight-${timestamp(insight.created_at)}`),
    createdAt: insight.created_at || null,
    ts: timestamp(insight.created_at),
    focusInsightId: insight._id ? String(insight._id) : null,
    paragraphs,
    actions,
    progression: isActivityReply ? null : buildCoachProgression(insight),
  };
};

const coachMessageFromConversation = (message, index) => {
  if (!['coach', 'assistant'].includes(message?.role)) return null;
  if (message.message_type === 'manager_report') return null;
  const text = String(message.text || '').trim();
  const actions = Array.isArray(message.actions) ? message.actions : [];
  if (!text && actions.length === 0) return null;
  return {
    id: String(message._id || `conversation-${timestamp(message.created_at)}-${index}`),
    createdAt: message.created_at || null,
    ts: timestamp(message.created_at),
    focusInsightId: null,
    paragraphs: text ? [text] : [],
    actions,
    progression: null,
  };
};

export const findLatestCoachMessage = (home = {}, fallbackRecommendation = null, { settled = true, dayKey, timeZone } = {}) => {
  // Only Today opts into a day boundary; never alter canonical history.
  // Use publication time, not an activity's older timeline anchor.
  const eligible = message => {
    if (!message || !dayKey) return Boolean(message);
    if (!message.createdAt || !Number.isFinite(new Date(message.createdAt).getTime())) return false;
    return coachDayKey(message.createdAt, timeZone) === dayKey;
  };
  const insights = (Array.isArray(home?.insights) ? home.insights : [])
    .map(insight => ({ insight, actions: buildCoachInsightActions(insight) }));
  const timeline = buildCoachTimeline({
    conversation: Array.isArray(home?.conversation) ? home.conversation : [],
    insights,
    activityEvents: Array.isArray(home?.activity_events) ? home.activity_events : [],
    proposals: Array.isArray(home?.proposals) ? home.proposals : [],
  });
  for (let index = timeline.length - 1; index >= 0; index -= 1) {
    const item = timeline[index];
    if (item.kind === 'insight') {
      const message = coachMessageFromInsight(item.insight);
      if (eligible(message)) return message;
    }
    if (item.kind === 'message') {
      const message = coachMessageFromConversation(item.msg, index);
      if (eligible(message)) return message;
    }
  }
  // A daily recommendation is not a latest-message snapshot. Only use it once
  // the canonical read has settled empty or failed, never as speculative prose.
  const fallback = settled ? coachMessageFromInsight(fallbackRecommendation) : null;
  return eligible(fallback) ? fallback : null;
};
